import React, { Fragment, useState } from 'react';

import dayjs from '~/timeInit';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { TourOfferPricingDeparture, TourOfferPricingLevel, TourOfferPricingPrice } from '~/types/tours/tourOffer';

import currencyFormatter from '~/utils/currencyFormatter';

import NativeTourPricingLevelField from './NativeTourPricingLevelField';
import {
  LEVEL_SOURCE_LABELS,
  PricingRow,
  RoomField,
  adjustmentSource,
  fromPercent,
  levelOf,
  luxPlusSource,
  priceProblems,
  toPercent,
  withRoomField,
} from './nativeTourPricing';

interface Props {
  rows: Array<PricingRow>;
  roomTypes: Array<string>;
  tourLevel: TourOfferPricingLevel;
  /** Problems show only on prices that were edited. */
  isEdited: (priceId: string) => boolean;
  onChange: (tourOptionId: string, departure: TourOfferPricingDeparture) => void;
}

const AUD = 'AUD';
const ROOM_COLUMNS = 6;
const BORDER = { borderLeft: 1, borderColor: 'divider' } as const;
const PERCENT_FIELDS: Array<{ field: RoomField; label: string }> = [
  { field: 'discount', label: 'Discount %' },
  { field: 'markup', label: 'Markup %' },
  { field: 'luxPlusDiscount', label: 'LUX+ %' },
];

const formatDate = (date: string) => dayjs(date).format('D MMM YYYY');

function describe(level: TourOfferPricingLevel): string {
  if (level.discount !== null) {
    return `${toPercent(level.discount)}% off`;
  }
  if (level.markup !== null) {
    return `${toPercent(level.markup)}% markup`;
  }
  return 'full price';
}

/** Dynamic Pricing V2's table: a row per departure, each room's prices and its own discount, markup and LUX+. */
export default function NativeTourPricingTable({ rows, roomTypes, tourLevel, isEdited, onChange }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const columnCount = 3 + roomTypes.length * ROOM_COLUMNS;

  const percentInput = (
    tourOptionId: string,
    departure: TourOfferPricingDeparture,
    price: TourOfferPricingPrice,
    field: RoomField,
  ) => {
    const problem = isEdited(price.priceId) ? priceProblems(price)[0] : undefined;
    const isDisabled = field === 'luxPlusDiscount' && price.luxPlusExactPrice !== null;
    return (
      <Tooltip title={isDisabled ? 'This room has its own LUX+ price' : (problem ?? '')}>
        <TextField
          size="small"
          type="number"
          value={toPercent(price[field])}
          disabled={isDisabled}
          error={!!problem && field !== 'luxPlusDiscount'}
          onChange={(event) =>
            onChange(tourOptionId, {
              ...departure,
              prices: departure.prices.map((other) =>
                other.priceId === price.priceId ? withRoomField(other, field, fromPercent(event.target.value)) : other,
              ),
            })
          }
          inputProps={{ min: 0, step: 1, 'aria-label': `${price.roomType} ${field}` }}
          sx={{ width: 76 }}
        />
      </Tooltip>
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell colSpan={3} />
            {roomTypes.map((roomType) => (
              <TableCell key={roomType} colSpan={ROOM_COLUMNS} align="center" sx={BORDER}>
                {roomType}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Departure</TableCell>
            <TableCell>Tour option</TableCell>
            {roomTypes.map((roomType) => (
              <Fragment key={roomType}>
                <TableCell align="right" sx={BORDER}>
                  Standard
                </TableCell>
                <TableCell align="right">Display</TableCell>
                <TableCell align="right">Member</TableCell>
                {PERCENT_FIELDS.map(({ field, label }) => (
                  <TableCell key={field}>{label}</TableCell>
                ))}
              </Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} align="center">
                No departures match.
              </TableCell>
            </TableRow>
          )}
          {rows.map(({ option, departure }) => {
            const isOpen = openId === departure.departureId;
            return (
              <Fragment key={departure.departureId}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <Tooltip title="This departure's own adjustment and LUX+ prices">
                      <IconButton
                        size="small"
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        onClick={() => setOpenId(isOpen ? null : departure.departureId)}
                      >
                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(departure.startDate)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{option.name}</TableCell>
                  {roomTypes.map((roomType) => {
                    const price = departure.prices.find((candidate) => candidate.roomType === roomType);
                    if (!price) {
                      return (
                        <TableCell key={roomType} colSpan={ROOM_COLUMNS} align="center" sx={BORDER}>
                          <Typography variant="body2" color="text.secondary">
                            No {roomType} price
                          </Typography>
                        </TableCell>
                      );
                    }
                    return (
                      <Fragment key={roomType}>
                        <TableCell align="right" sx={BORDER}>
                          {currencyFormatter(AUD, price.fullPrice)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              price.displayPrice > price.fullPrice
                                ? 'success.main'
                                : price.displayPrice < price.fullPrice
                                  ? 'error.main'
                                  : undefined,
                          }}
                        >
                          {currencyFormatter(AUD, price.displayPrice)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'info.main' }}>
                          {currencyFormatter(AUD, price.memberPrice)}
                        </TableCell>
                        {PERCENT_FIELDS.map(({ field }) => (
                          <TableCell key={field}>
                            {percentInput(option.tourOptionId, departure, price, field)}
                          </TableCell>
                        ))}
                      </Fragment>
                    );
                  })}
                </TableRow>
                {isOpen && (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ bgcolor: 'action.hover' }}>
                      <Stack spacing={2} py={1}>
                        <Box>
                          <Typography variant="subtitle2" mb={1}>
                            This departure ({formatDate(departure.startDate)} to {formatDate(departure.endDate)})
                          </Typography>
                          <NativeTourPricingLevelField
                            value={levelOf(departure)}
                            onChange={(level) => onChange(option.tourOptionId, { ...departure, ...level })}
                            followText="Follow the option"
                          />
                        </Box>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                          {departure.prices.map((price) => {
                            const taken = adjustmentSource(tourLevel, levelOf(option), departure, price);
                            const luxPlus = luxPlusSource(tourLevel, levelOf(option), departure, price);
                            const exactProblem = isEdited(price.priceId)
                              ? priceProblems(price).find((problem) => problem.includes('LUX+ price'))
                              : undefined;
                            return (
                              <Stack key={price.priceId} spacing={1}>
                                <Typography variant="body2" fontWeight={500}>
                                  {price.roomType}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Takes {describe(taken.level)} from {LEVEL_SOURCE_LABELS[taken.source].toLowerCase()},
                                  LUX+ from {LEVEL_SOURCE_LABELS[luxPlus].toLowerCase()}
                                </Typography>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="LUX+ price"
                                  value={price.luxPlusExactPrice ?? ''}
                                  disabled={price.luxPlusDiscount !== null}
                                  helperText={
                                    exactProblem ??
                                    (price.luxPlusDiscount !== null ? 'Clear the LUX+ % first' : undefined)
                                  }
                                  error={!!exactProblem}
                                  onChange={(event) =>
                                    onChange(option.tourOptionId, {
                                      ...departure,
                                      prices: departure.prices.map((other) =>
                                        other.priceId === price.priceId
                                          ? {
                                              ...other,
                                              luxPlusExactPrice:
                                                event.target.value === '' ? null : Number(event.target.value),
                                            }
                                          : other,
                                      ),
                                    })
                                  }
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  sx={{ width: 200 }}
                                />
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
